<template>
    <step-wrapper>
        <step-heading class="flex items-center justify-between">
            {{ $t('CUSTOM_TEXT') }}
            <button
                class="rounded text-[14px] font-normal uppercase underline focus-outline-only"
                @click="clearPlacement('Text')"
            >
                {{ $t('CLEAR') }}
            </button>
        </step-heading>

        <template v-if="placementOptions.length > 1">
            <step-section-heading>
                {{ $t('DECORATION_LOCATION') }}
            </step-section-heading>

            <div class="mb-[24px] flex gap-[12px]">
                <primary-button
                    v-for="option in placementOptions"
                    :key="option.code"
                    :is-active="option.selected"
                    @click="selectPlacement(option)"
                >
                    {{option.name}}
                </primary-button>
            </div>
        </template>

        <step-section-heading id="customTextLabel">
            {{ $t('TYPE_YOUR_TEXT') }}
        </step-section-heading>

        <input
            type="text"
            class="w-full rounded border border-gray-300 p-4 font-caption text-base focus-outline-only"
            :placeholder="$t('TYPE_YOUR_TEXT_HERE')"
            :maxlength="allowedNumberOfCharacters"
            v-model="text"
            aria-labelledby="customTextLabel"
            @focus="openKeyboard"
            @blur="closeKeyboard"
        />
        <div
            v-if="validationError"
            class="my-[8px] text-red"
        >
            {{ $t('VALIDATION_ERROR') }}
        </div>
        <div class="mb-[24px] mt-[8px] flex font-sans text-sm font-normal text-gray-dark">
            <div>
                {{ numberOfRemainingCharacters }}
            </div>&nbsp;
            <div>
                {{ $t('CHARACTERS_REMAINING') }}
            </div>
        </div>

        <step-section-heading>
            {{ $t('FONT') }}
        </step-section-heading>

        <div class="mb-[24px] flex gap-[12px]">
            <drop-down
                :options="fontOptionsForDropdown"
                :placeholder="$t('SELECT_FONT')"
                @select="selectFont"
                :selected-value="selectedFont"
                prefix-icon="FontIcon"
            />
        </div>
    </step-wrapper>
</template>

<script>
    import StepHeading from '../UI/headings/StepHeading.vue';
    import StepWrapper from '../wrappers/StepWrapper.vue';
    import StepSectionHeading from '../UI/headings/StepSectionHeading.vue';
    import PrimaryButton from '../UI/buttons/PrimaryButton.vue';
    import DropDown from '../UI/dropdowns/DropDown.vue';
    import allStores from '../../stores/all';

    export default {
        mixins: [allStores],

        name: 'TextStep',

        components: {
            StepHeading,
            StepWrapper,
            StepSectionHeading,
            PrimaryButton,
            DropDown,
        },

        data() {
            return {
                text: '',
                lastAcceptedText: '',
                validationError: false,
                changeCounter: 0,
                debounceTimeout: null,
            };
        },

        computed: {
            selectedFont() {
                return this.fontOptions.find((x) => x.code === this.viewController.selectedAtPlacement('Text')?.description?.find(
                    (y) => y.definition === 'Text Font',
                )?.code)?.code;
            },

            fontOptionsForDropdown() {
                return this.fontOptions.map((option) => ({
                    label: option.name,
                    value: option.code,
                    style: {
                        fontFamily: option.code,
                    },
                }));
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            textOptions() {
                const options = this.customizerStore.textOptions;

                return options;
            },

            placementOptions() {
                const list = [];

                const placement = this.textOptions.find((x) => x.description.definition === 'Text Placement');

                if (placement == null) {
                    return list;
                }

                for (const option of placement.options) {
                    list.push({
                        name: option.option.name,
                        code: option.option.code,
                        selected: option.selected,
                    });
                }

                return list;
            },

            fontOptions() {
                const list = [];

                const placement = this.textOptions.find((x) => x.description.definition === 'Text Font');

                if (placement == null) {
                    return list;
                }

                for (const option of placement.options) {
                    list.push({
                        name: option.option.name,
                        code: option.option.code,
                        selected: option.selected,
                    });
                }

                return list;
            },

            selectedOption() {
                const selected = this.viewController.selectedAtPlacement('Text');

                return selected;
            },

            allowedNumberOfCharacters() {
                return +(this.selectedOption?.custom['personalization-limit'] ?? '0');
            },

            numberOfRemainingCharacters() {
                return this.allowedNumberOfCharacters - this.text.length;
            },

            isMobile() {
                return this.appStore.isMobile;
            },

            selectedGroupItem() {
                return this.customizerStore.selectedGroupItem;
            },
        },

        watch: {
            text(newValue, oldValue) {
                this.selectText(newValue, oldValue);
            },
        },

        mounted() {
            this.readTextFromController();
            this.loadFontFaces();
            this.selectView();
        },

        methods: {
            selectView() {
                const useView = this.viewController?.blueprint?.custom?.['personalization-view'];
                if (useView) {
                    const view = this.viewController.blueprint.views.find((x) => x.code === useView);
                    if (view) {
                        this.customizerStore.selectView(view);
                    }
                }
            },

            async clearPlacement(placementCode) {
                this.customizerStore.clearPlacement(placementCode);
                this.text = '';
                this.lastAcceptedText = '';
                this.validationError = false;
                this.changeCounter = 0;
                this.debounceTimeout = null;

                this.readTextFromController();
                this.loadFontFaces();

                await this.$nextTick();

                this.selectFont(this.fontOptions[0].code);
            },

            loadFontFaces() {
                this.fontOptions.forEach(async (option) => {
                    try {
                        const font = new FontFace(
                            option.code,
                            `url(https://fx.images.drivecommerce.com/api/v1/ridge/image/${option.code}.woff2)`,
                        );
                        const loadedFont = await font.load();
                        document.fonts.add(loadedFont);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(`Failed to load font ${option.code}:`, error);
                    }
                });
            },

            readTextFromController() {
                const currentText = this.viewController.selectedAtPlacement('Text');

                this.text = currentText?.custom?.['personalization-text'] ?? '';
                this.lastAcceptedText = this.text;
            },

            selectPlacement(option) {
                this.viewController.updateSimilarComponent('Text', 'Text Placement', option.code);
            },

            selectFont(code) {
                this.viewController.updateSimilarComponent('Text', 'Text Font', code);
            },

            async selectText(newValue) {
                if (this.debounceTimeout) {
                    clearTimeout(this.debounceTimeout);
                }

                this.changeCounter += 1;

                const currentChange = this.changeCounter;

                // Debounce the validation.
                this.debounceTimeout = setTimeout(async () => {
                    try {
                        // If user typed something, abort this validation.
                        if (currentChange !== this.changeCounter) {
                            return;
                        }

                        let reviewResult = {
                            hasProfanity: false,
                        };

                        if (newValue.length > 0) {
                            reviewResult = await this.viewController.reviewText(newValue);
                        }

                        // If another change occurred during the review, also abort.
                        if (currentChange !== this.changeCounter) {
                            return;
                        }

                        if (!reviewResult.hasProfanity && !reviewResult.reviewRecommended) {
                            // Commit changes.
                            const updateResult = await this.viewController.updatePersonalization('Text', newValue);

                            // If another change occurred during update, abort
                            if (currentChange !== this.changeCounter) {
                                return;
                            }

                            if (updateResult.accepted) {
                                if (updateResult.acceptedAs != null) {
                                    this.text = updateResult.acceptedAs;
                                }

                                // Clear monogramming since these both are mutually exclusive.
                                this.customizerStore.clearPlacement('Monogram');

                                this.viewController.clearPlacementTags('Text');

                                if (this.text.length > 0) {
                                    this.viewController.addPlacementTag('Text', 'personalized');
                                }

                                this.viewController.updateComponents();

                                if (this.selectedGroupItem) {
                                    this.customizerStore.updateGroupPersonalization(this.selectedGroupItem, this.text);
                                }

                                this.lastAcceptedText = this.text;
                                this.validationError = false;
                            } else {
                                // Revert to last accepted text if not accepted.
                                this.text = this.lastAcceptedText;
                                // This is not a content error.
                                this.validationError = false;
                            }
                        } else {
                            // Revert if profanity found.
                            // this.text = this.lastAcceptedText;
                            this.validationError = true;
                        }
                    // eslint-disable-next-line no-unused-vars
                    } catch (error) {
                        // this.text = this.lastAcceptedText;
                        this.validationError = true;
                    }
                }, 300);
            },

            openKeyboard() {
                if (this.isMobile) {
                    this.appStore.toggleKeyboard(true);
                }
            },

            closeKeyboard() {
                if (this.isMobile) {
                    this.appStore.toggleKeyboard(false);
                }
            },
        },
    };
</script>
